import {
  Button,
  DatePicker,
  Divider,
  Flex,
  Modal,
  ModalProps,
  Select,
  Typography,
} from "antd"
import { useTranslation } from "react-i18next"
import styles from "./styles.module.scss"
import { Controller, UseFormReturn } from "react-hook-form"
import { FiltersForm, RangeValue } from "./use-form"
import { FormControl } from "@atoms"
import { useSearchParams } from "react-router-dom"
import dayjs, { Dayjs } from "dayjs"

const { Option } = Select
const { RangePicker } = DatePicker

const { Text } = Typography

interface Props extends ModalProps {
  onClose: () => void
  onSubmit: (e?: React.BaseSyntheticEvent) => Promise<void>
  form: UseFormReturn<FiltersForm, any>
}

const FilterModal: React.FC<Props> = ({ form, onSubmit, ...props }) => {
  const { t } = useTranslation()
  let [searchParams] = useSearchParams()

  const {
    control,
    resetField,
    watch,
    getValues,
    setValue,
    reset,
    formState: { isDirty },
  } = form

  return (
    <Modal
      title={t("global.filters")}
      onCancel={props.onClose}
      footer={[
        <Button
          key="back"
          onClick={() => {
            reset({
              status: searchParams.get("status") ?? undefined,
              start_date: searchParams.get("start_date[values][0]")
                ? {
                    operator: searchParams.get("start_date[operator]")!,
                    values: [
                      dayjs(searchParams.get("start_date[values][0]")),
                      searchParams.get("start_date[values][1]")
                        ? dayjs(searchParams.get("start_date[values][1]"))
                        : null,
                    ].filter((date) => !!date) as RangeValue<Dayjs | null>,
                  }
                : { operator: "equals" },
            })
            props.onClose()
          }}
        >
          {t("global.cancel")}
        </Button>,
        <Button
          key="reset"
          type="primary"
          danger
          onClick={async () => {
            setValue("status", undefined)
            if (getValues("start_date.operator") !== "between")
              setValue("start_date.values.0", null)
            else setValue("start_date.values", null)
            resetField("start_date.operator")
          }}
        >
          {t("global.reset")}
        </Button>,
        <Button
          key="submit"
          htmlType="submit"
          type="primary"
          onClick={onSubmit}
          disabled={!isDirty}
        >
          {t("global.apply")}
        </Button>,
      ]}
      wrapClassName={styles.form}
      {...props}
    >
      <Flex vertical style={{ padding: "24px 0" }}>
        <FormControl className={styles.formGroup}>
          <span style={{ minWidth: 100 }}>
            <Text strong>{t("labels.status")}</Text>
          </span>
          <div className={styles.inputContainer}>
            <Controller
              name="status"
              control={control}
              render={({ field }) => (
                <Select
                  {...field}
                  placeholder={t("select", {
                    entity: t("labels.status"),
                    context: "male",
                  })}
                  style={{ width: "100%" }}
                  allowClear
                >
                  <Option value="approved">{t("events.approved")}</Option>
                  <Option value="execution">{t("events.execution")}</Option>
                </Select>
              )}
            />
          </div>
        </FormControl>
        <Divider style={{ margin: "12px 0" }} />
        <FormControl className={styles.formGroup}>
          <span style={{ minWidth: 100 }}>
            <Text strong>{t("date.date")}</Text>
          </span>
          <Flex vertical gap={8}>
            <Controller
              name="start_date.operator"
              control={control}
              render={({ field }) => (
                <Select
                  {...field}
                  onChange={(value) => {
                    field.onChange(value)
                    setValue("start_date.values", null)
                  }}
                  style={{ width: "100%" }}
                >
                  <Option value="equals">{t("date.equals")}</Option>
                  <Option value="lt">{t("date.lessThan")}</Option>
                  <Option value="gt">{t("date.greaterThan")}</Option>
                  <Option value="between">{t("date.range")}</Option>
                </Select>
              )}
            />
            {watch("start_date.operator") !== "between" ? (
              <Controller
                name="start_date.values.0"
                control={control}
                render={({ field }) => (
                  <DatePicker {...field} style={{ width: "100%" }} />
                )}
              />
            ) : (
              <Controller
                name="start_date.values"
                control={control}
                render={({ field }) => (
                  <RangePicker
                    {...field}
                    popupClassName={styles.singleMonthRangePicker}
                  />
                )}
              />
            )}
          </Flex>
        </FormControl>
      </Flex>
    </Modal>
  )
}

export default FilterModal
